import { useTilledPaymentMethodCreateMutation } from '@bambi/api-client/bambi';
import type {
  Invoice,
  InvoicePayment,
  InvoicePaymentWritable,
} from '@bambi/api-client/schemas';
import { Button } from '@bambi/component-library/src/components/Button/Button';
import { DefaultFormFieldLayout } from '@bambi/component-library/src/components/forms/DefaultFormFieldLayout/DefaultFormFieldLayout';
import { Select } from '@bambi/component-library/src/components/inputs/Select/Select';
import type { SelectOption } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';
import { Skeleton } from '@bambi/component-library/src/components/Skeleton/Skeleton';
import { throttle } from 'lodash-es';
import { DateTime } from 'luxon';
import type { InputHTMLAttributes } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { formatBillingServerError } from '../../helpers/formatBillingServerError';
import {
  setEditingPaymentLinkId,
  setShowPaymentLinkModal,
} from '../invoice.slice';
import { useHydratedInvoice } from '../useHydratedInvoice';
import { usePaymentLink } from './hooks/usePaymentLink';
import { usePaymentPayers } from './hooks/usePaymentPayers';
import { PaymentLinkModal } from './PaymentLinkModal';
import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { CurrencyInput } from 'src/common/CurrencyInput';
import { FormFieldTextArea } from 'src/common/FormField/FormFieldTextArea';
import { ManagePaymentMethods } from 'src/common/forms/PaymentMethodCaptureForm/ManagePaymentMethods';
import { usePaymentMethodCaptureContext } from 'src/common/forms/PaymentMethodCaptureForm/PaymentMethodCaptureContext';
import type { BillingDetails } from 'src/common/forms/PaymentMethodCaptureForm/PaymentMethodCaptureForm';
import { validateBillingAddress } from 'src/common/forms/PaymentMethodCaptureForm/PaymentMethodCaptureForm';
import { useCanCapturePaymentMethod } from 'src/common/forms/PaymentMethodCaptureForm/useCanCapturePaymentMethod';
import { useTilled } from 'src/common/forms/PaymentMethodCaptureForm/useTilled';
import { Modal } from 'src/common/primitives/Modal';
import { show } from 'src/common/primitives/Toast/toast.slice';
import { Well } from 'src/common/primitives/Well/Well';

interface PaymentModalProps {
  open: boolean;
  invoice: Invoice | null;
  loading?: boolean;
  setOpen: (open: boolean) => void;
  onConfirm: (data: InvoicePaymentWritable) => void;
}
export function PaymentModal({
  open,
  invoice,
  setOpen,
  loading,
  onConfirm,
}: PaymentModalProps) {
  const dispatch = useAppDispatch();

  const { onlyInvoicingEnabled } = useCanCapturePaymentMethod();
  const { isLoading, data } = useHydratedInvoice(invoice);

  const { isLoadingPayers, payerOptions, selfPayer } = usePaymentPayers(
    invoice?.id
  );

  const {
    getValues,
    setValue,
    reset,
    resetField,
    control,
    setError,
    handleSubmit,
    formState,
    watch,
  } = useForm<InvoicePayment & { payer?: SelectOption }>({
    defaultValues: {
      amount_cents: invoice?.total_amount_due_cents,
      date: DateTime.now().toISODate(),
      external_payment_method_id: '',
      invoice: invoice?.id,
      tilled_type_of_payer: '',
      status: 'requires_capture',
      payer: selfPayer,
      is_manual: false,
      note: '',
    },
  });

  const passengerId = useMemo(() => {
    if (isLoading) {
      return '';
    }

    return data?.line_items.find(
      (lineItem) => lineItem.line_item_type === 'trip'
    )?.trip.passenger.id;
  }, [isLoading, data]);

  useEffect(() => {
    setValue('by_passenger', passengerId);
  }, [passengerId, setValue]);

  // Reset after invoice data is resolved
  useEffect(() => {
    if (isLoading || isLoadingPayers) {
      return;
    }

    reset({
      date: DateTime.now().toISODate(),
      external_payment_method_id: '',
      by_passenger: passengerId,
      by_payer: null,
      amount_cents: invoice?.total_amount_due_cents ?? 0,
      invoice: invoice?.id,
      payer: selfPayer,
    });
  }, [reset, isLoading, isLoadingPayers, invoice, passengerId, selfPayer]);

  // Transform selected payer into payer_id and type
  const handleSetPayer = useCallback(
    (newPayer?: SelectOption) => {
      let payerId: string;

      if (!newPayer) {
        setValue('payer', newPayer);
        return;
      }

      let key: keyof InvoicePaymentWritable = 'by_payer';
      if (newPayer === selfPayer) {
        payerId = passengerId ?? '';
        key = 'by_passenger';
        setValue('by_payer', null);
      } else {
        payerId = newPayer.value;
        key = 'by_payer';
        setValue('by_passenger', null);
      }

      setValue('payer', newPayer);
      setValue(key, payerId);
      resetField('external_payment_method_id');
    },
    [passengerId, selfPayer, setValue, resetField]
  );

  // Store billing details to save
  const [billingDetails, setBillingDetails] = useState<BillingDetails>();

  const { savePaymentMethod, validateForm: validatePaymentForm } =
    useTilled('card');
  const [savePaymentMethodCreate] = useTilledPaymentMethodCreateMutation();
  const { areFieldsEmpty } = usePaymentMethodCaptureContext();
  const [savingPaymentMethod, setSavingPaymentMethod] = useState(false);

  const byPassenger = watch('by_passenger');
  const byPayer = watch('by_payer');
  const payer = watch('payer');

  // Calculate the current payerId/type
  const payerId = byPassenger || byPayer;
  const payerType =
    payer?.value === selfPayer?.value ? 'passenger' : 'payer_organization';

  const handleSubmitPayment = useCallback(
    async (formData: InvoicePaymentWritable) => {
      const billingAddress = {
        city: billingDetails?.city,
        country: billingDetails?.country,
        zip: billingDetails?.zip,
        state: billingDetails?.state,
        street: billingDetails?.street,
        street2: billingDetails?.street2,
      };

      const validBillingAddress = validateBillingAddress(billingAddress);

      const validPaymentForm = await validatePaymentForm();
      const billingName = billingDetails?.billingName ?? '';

      if (!areFieldsEmpty() && !!billingDetails) {
        // Creating a new payment?
        // Attempt to save payment method if valid
        if (validPaymentForm && billingName) {
          setSavingPaymentMethod(true);
          try {
            // Persist payment method in tilled
            const savedPaymentMethod = await savePaymentMethod({
              type: 'card',
              billing_details: {
                name: billingName,
                address: validBillingAddress
                  ? {
                      ...validBillingAddress,
                      zip: validBillingAddress.zip,
                    }
                  : undefined,
              },
            });

            // Shouldn't happen in practice, either the payer or passenger will have an id
            if (!payerId) {
              setSavingPaymentMethod(false);
              dispatch(
                show({
                  title: 'Unable to save payment method',
                  description: 'Missing payer id',
                  type: 'error',
                })
              );
              return;
            }

            // Attach payment method to payer
            await savePaymentMethodCreate({
              tilledPaymentMethodInput: {
                payer_id: payerId,
                payer_type: payerType,
                payment_method_id: savedPaymentMethod.id,
              },
            }).unwrap();

            setSavingPaymentMethod(false);

            dispatch(
              show({
                title: 'Saved payment method',
                type: 'success',
              })
            );

            onConfirm({
              ...formData,
              external_payment_method_id: formData.is_manual
                ? '-1'
                : savedPaymentMethod.id,
            });
          } catch (e) {
            setSavingPaymentMethod(false);
            dispatch(
              show({
                title: 'Failed to save payment method',
                description: formatBillingServerError(e),
                type: 'error',
              })
            );
          }
        } else {
          setSavingPaymentMethod(false);
          dispatch(
            show({
              type: 'error',
              title: 'Unable to save payment method',
              description: 'Payment details are invalid, or missing',
            })
          );
        }
      } else if (getValues().amount_cents <= 0) {
        setSavingPaymentMethod(false);
        setError('amount_cents', {
          message: 'Amount should be greater than zero',
        });
      }
      // Validate a payment method was selected, or error
      else if (!getValues().external_payment_method_id) {
        setSavingPaymentMethod(false);
        setError('external_payment_method_id', {
          message: 'Select a payment method',
        });
      } else {
        // Create payment using existing payment method
        return onConfirm(formData);
      }
    },
    [
      billingDetails,
      validatePaymentForm,
      areFieldsEmpty,
      getValues,
      savePaymentMethod,
      payerId,
      savePaymentMethodCreate,
      payerType,
      dispatch,
      onConfirm,
      setError,
      setSavingPaymentMethod,
    ]
  );

  const throttledHandleSubmitPayment = useMemo(
    () => throttle(handleSubmitPayment, 100),
    [handleSubmitPayment]
  );

  const showPaymentLinkModal = useAppSelector(
    (state) => state.invoice.showPaymentLinkModal
  );
  const { isLoading: isLoadingPaymentLink, showPaymentLink } =
    usePaymentLink(invoice);

  const handleSetOpenAndReset = useCallback(
    (open: boolean) => {
      // Reset extra components on open change
      dispatch(setEditingPaymentLinkId(''));
      dispatch(setShowPaymentLinkModal(false));
      setOpen(open);
    },
    [dispatch, setOpen]
  );

  return (
    <>
      <Modal
        open={open}
        setOpen={handleSetOpenAndReset}
        contentClassnames="w-full max-w-[45vw]"
        title="Pay Invoice"
      >
        <h3 className="text-[24px] font-medium leading-6 text-gray-900">
          Pay Invoice
        </h3>
        <div className="mt-4 flex flex-row items-center justify-start gap-8">
          <Controller
            control={control}
            name="payer"
            render={({ field }) => {
              if (isLoadingPayers) {
                return (
                  <DefaultFormFieldLayout label="Payer *" inputId="payer">
                    <Skeleton />
                  </DefaultFormFieldLayout>
                );
              }

              return (
                <DefaultFormFieldLayout label="Payer *" inputId="payer">
                  <Select
                    options={payerOptions}
                    onChange={handleSetPayer}
                    value={field.value?.value}
                    contentWidth="250px"
                  />
                </DefaultFormFieldLayout>
              );
            }}
          />
        </div>

        <Well>
          <div className="flex flex-row justify-between">
            <div className="text-xl">Amount</div>
            <div className="text-xl">
              <Controller
                control={control}
                name="amount_cents"
                render={({ field, fieldState }) => {
                  return (
                    <div className="flex flex-col gap-2">
                      <CurrencyInput
                        value={field.value}
                        onValueChange={(e) => field.onChange(e.floatValue)}
                      />
                      {fieldState.error?.message && (
                        <strong className="text-sm text-error-700">
                          {fieldState.error.message}
                        </strong>
                      )}
                    </div>
                  );
                }}
              />
            </div>
          </div>
          <div className="flex flex-row justify-between">
            <div className="text-lg">Note</div>
            <Controller
              control={control}
              name="note"
              render={({ field, fieldState }) => {
                return (
                  // TODO: Replace with ContextFormFieldTextArea
                  // It doesn't currently support customizing the label text size
                  <FormFieldTextArea
                    type="textarea"
                    inputProps={
                      field as unknown as InputHTMLAttributes<HTMLInputElement>
                    }
                    error={fieldState.error?.message}
                  />
                );
              }}
            />
          </div>
        </Well>
        {onlyInvoicingEnabled ? null : (
          <Well>
            <div className="flex flex-row">
              <div className="text-xl">Credit Card</div>
            </div>
            <Controller
              name="external_payment_method_id"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <strong className="text-error-700">
                    {fieldState.error?.message}
                  </strong>
                  <div className="sm:col-span-5">
                    <ManagePaymentMethods
                      payerId={payerId ?? ''}
                      payerType={payerType}
                      billingName={billingDetails?.billingName ?? ''}
                      selectedPaymentMethodId={field.value}
                      onChangeBillingDetails={setBillingDetails}
                      onSelectPaymentMethod={field.onChange}
                    />
                  </div>
                </>
              )}
            />
          </Well>
        )}

        <div className="mt-4 flex flex-row justify-between">
          <Button
            disabled={!formState.isValid}
            dataTestId="cancelBtn"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          <div className="flex flex-row gap-2">
            {!onlyInvoicingEnabled ? (
              <Button
                variant="secondary-outline"
                loading={isLoadingPaymentLink || isLoadingPayers}
                disabled={!payerId}
                onClick={() => {
                  showPaymentLink({
                    payerId: payerId!,
                    payerType,
                  });
                }}
              >
                Payment Link
              </Button>
            ) : null}
            <Button
              disabled={!formState.isValid}
              type="submit"
              variant={onlyInvoicingEnabled ? 'primary' : 'primary-outline'}
              onClick={(e) => {
                setValue('is_manual', true);
                setValue('external_payment_method_id', '-1');
                handleSubmit(throttledHandleSubmitPayment)(e);
              }}
              dataTestId="manual-payment-button"
              loading={loading}
            >
              Mark as Paid
            </Button>
            {onlyInvoicingEnabled ? null : (
              <Button
                disabled={!formState.isValid || isLoadingPayers}
                type="submit"
                variant="primary"
                onClick={handleSubmit(throttledHandleSubmitPayment)}
                dataTestId="confirmBtn"
                loading={loading || savingPaymentMethod}
              >
                Confirm
              </Button>
            )}
          </div>
        </div>
      </Modal>
      <PaymentLinkModal
        open={showPaymentLinkModal}
        setOpen={(open) => dispatch(setShowPaymentLinkModal(open))}
        payerType={payerType}
        payerId={payerId as unknown as string}
      />
    </>
  );
}
